/**
 * ImportModal Mini-Store State
 *
 * Manages the import publications modal: mode selection, publication selection,
 * server-side selection model, and ID input for "Import by IDs" mode.
 * Reset on close via imperative dispatch from the parent component.
 */

export type ChannelPublicationPreview = {
  id: string;
  title: string;
  thumbnail: string | null;
  category: string | null;
  isImported: boolean;
  importedAt: string | null;
};

export type ImportModalState = {
  /** Current wizard step (0 = closed, 1 = mode selection, 2 = publication selection) */
  step: 0 | 1 | 2;

  /** Channel info (set on open, used by policies) */
  connectorId: string | null;
  integrationUserId: string | null;

  /** Import mode */
  importMode: 'all' | 'custom' | 'byIds';

  /** Preview build state */
  preview: {
    previewId: string | null;
    status: 'idle' | 'building' | 'ready' | 'error' | 'stopped';
    progress: { fetched: number; total: number };
    errorMessage: string | null;
    isResumed: boolean;
  };

  /** Current page of publications */
  publications: ChannelPublicationPreview[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalNotImported: number;
    totalImported: number;
  };

  /** Filters & search */
  filters: {
    importStatus: 'all' | 'imported' | 'notImported';
    category: string | null;
  };
  search: string;
  availableCategories: string[];

  /** Server-side selection model */
  selection: {
    mode: 'manual' | 'allInFilter';
    manualSelectedIds: string[];
    manualDeselectedIds: string[];
    filterSnapshot: {
      importStatus: 'all' | 'imported' | 'notImported';
      category: string | null;
      search: string;
    } | null;
  };

  /** ID input for "Import by IDs" mode */
  idInput: {
    rawText: string;
    parsedIds: string[];
    validationResults: Record<string, 'checking' | 'found' | 'notFound'>;
  };

  /** Global loading flag */
  isLoading: boolean;
};

export const initialState: ImportModalState = {
  step: 0,
  connectorId: null,
  integrationUserId: null,
  importMode: 'all',
  preview: {
    previewId: null,
    status: 'idle',
    progress: { fetched: 0, total: 0 },
    errorMessage: null,
    isResumed: false
  },
  publications: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalNotImported: 0,
    totalImported: 0
  },
  filters: {
    importStatus: 'all',
    category: null
  },
  search: '',
  availableCategories: [],
  selection: {
    mode: 'manual',
    manualSelectedIds: [],
    manualDeselectedIds: [],
    filterSnapshot: null
  },
  idInput: {
    rawText: '',
    parsedIds: [],
    validationResults: {}
  },
  isLoading: false
};
